import {
  ICompatibility,
  IItemSpecific,
} from "../../../models/SellerHistory/SellerSoldHistory";

// Preferred left-to-right ordering for the compatibility table. Any columns eBay
// returns that aren't listed here are appended afterwards in the order seen.
const PREFERRED_COMPAT_COLUMNS = [
  "year",
  "make",
  "model",
  "trim",
  "engine",
  "notes",
];

/** Resolves the ordered set of column keys present across all compatibility rows. */
export function compatibilityColumns(compat?: ICompatibility | null): string[] {
  if (!compat?.items?.length) return [];

  const seen = new Set<string>();
  compat.items.forEach((row) =>
    Object.keys(row).forEach((key) => seen.add(key))
  );

  const preferred = PREFERRED_COMPAT_COLUMNS.filter((c) => seen.has(c));
  const extras = Array.from(seen).filter(
    (c) => !PREFERRED_COMPAT_COLUMNS.includes(c)
  );
  return [...preferred, ...extras];
}

/** Title-cases a column key for display, e.g. "make" -> "Make". */
export function prettyColumn(key: string): string {
  return key
    .split(/[_\s]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Flattens item specifics into a single readable cell, e.g. "Brand: Bosch | Type: Filter". */
export function itemSpecificsToText(specifics?: IItemSpecific[]): string {
  if (!specifics?.length) return "";
  return specifics
    .map((s) => `${s.name}: ${s.value}`)
    .join(" | ");
}

/** Flattens compatibility into a single readable cell for CSV export. */
export function compatibilityToText(compat?: ICompatibility | null): string {
  if (!compat) return "";

  const columns = compatibilityColumns(compat);
  const rows = compat.items.map((row) =>
    columns
      .map((col) => row[col])
      .filter((v) => v && v.trim().length > 0)
      .join(" ")
      .trim()
  );

  const body = rows.filter((r) => r.length > 0).join("; ");

  // Lead with eBay's summary line when present so the cell reads on its own.
  if (compat.summary && body) return `${compat.summary} — ${body}`;
  return compat.summary || body;
}
